import {
  Button,
  Container,
  Nav,
  Navbar as NavbarBs,
  NavDropdown,
  Badge,
} from "react-bootstrap";
import { NavLink, useLocation } from "react-router-dom";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { useMemo } from "react";
import { useUserContext } from "../context/UserContext";
import { Cart3, Shop, Receipt, Person } from "react-bootstrap-icons";
import logo from "/imgs/logo/brand-image-250.png";

// ─── Claim resolution ─────────────────────────────────────────────────────────

const CLAIM_ORDER = ["elite", "operations", "sales", "merchant", "customer"];

const ROUTE_CLAIM_MAP = [
  ["/store/", "merchant"],
  ["/sup", "elite"],
  ["/ops", "operations"],
  ["/sales", "sales"],
];

// Paths that always render the customer menu regardless of auth claim
const CUSTOMER_ROUTE_PREFIXES = ["/stores", "/checkout"];

function claimFromClaims(claims) {
  return CLAIM_ORDER.find((c) => claims[c]) ?? "customer";
}

function claimFromPath(pathname) {
  return ROUTE_CLAIM_MAP.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? "customer";
}

// ─── Bottom-nav config ────────────────────────────────────────────────────────

const BOTTOM_NAV_CONFIG = {
  merchant: [
    { to: "/store/dashboard", label: "Dashboard", Icon: Shop, match: "/store" },
    { to: "/store/orders", label: "Orders", Icon: Receipt, match: "/store/orders" },
    { to: "/account", label: "Account", Icon: Person, match: "/account" },
  ],
  elite: [
    { to: "/sup/", label: "Home", Icon: Shop, match: "/sup" },
    { to: "/sup/stores", label: "Tables", Icon: Receipt, match: "/sup/stores" },
    { to: "/account", label: "Account", Icon: Person, match: "/account" },
  ],
  operations: [
    { to: "/ops/", label: "Home", Icon: Shop, match: "/ops" },
    { to: "/ops/deliveries", label: "Deliveries", Icon: Receipt, match: "/ops/deliveries" },
    { to: "/account", label: "Account", Icon: Person, match: "/account" },
  ],
  sales: [
    { to: "/sales/dashboard", label: "Dashboard", Icon: Shop, match: "/sales" },
    { to: "/sales/orders", label: "Orders", Icon: Receipt, match: "/sales/orders" },
    { to: "/sales/analytics", label: "Analytics", Icon: Person, match: "/sales/analytics" },
  ],
  customer: [
    { to: "/", label: "Stores", Icon: Shop, match: "/" },
    { to: "/account/orders", label: "Orders", Icon: Receipt, match: "/account/orders" },
    { to: "/account", label: "Account", Icon: Person, match: "/account" },
  ],
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const navStyles = {
  navbar: {
    backdropFilter: "blur(18px)",
    backgroundColor: "rgba(255, 255, 255, 0.88)",
    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
    paddingTop: "max(0.35rem, env(safe-area-inset-top))",
    paddingBottom: "0.35rem",
  },
  cartButton: {
    width: "2.65rem",
    height: "2.65rem",
    position: "relative",
    transition: "all 0.2s ease",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
    "--bs-btn-color": "#0d6efd",
    "--bs-btn-bg": "rgba(255, 255, 255, 0.92)",
    "--bs-btn-border-color": "rgba(15, 23, 42, 0.08)",
    "--bs-btn-hover-color": "#0d6efd",
    "--bs-btn-hover-bg": "rgba(13, 110, 253, 0.12)",
    "--bs-btn-hover-border-color": "rgba(13, 110, 253, 0.2)",
    "--bs-btn-active-color": "#fff",
    "--bs-btn-active-bg": "rgba(13, 110, 253, 0.2)",
    "--bs-btn-active-border-color": "rgba(13, 110, 253, 0.28)",
    "--bs-btn-focus-shadow-rgb": "13, 110, 253",
  },
  cartBadge: {
    width: "1.25rem",
    height: "1.25rem",
    fontSize: "0.75rem",
    position: "absolute",
    bottom: 0,
    right: 0,
    transform: "translate(25%, 25%)",
  },
  desktopNavLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "2.35rem",
    padding: "0.45rem 0.95rem",
    borderRadius: "999px",
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#111827",
    letterSpacing: "-0.01em",
  },
  bottomNav: {
    position: "fixed",
    left: "50%",
    transform: "translateX(-50%)",
    bottom: "0",
    width: "calc(100% - 24px)",
    maxWidth: "900px",
    padding: "10px",
    paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
    borderRadius: "12px 12px 0 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "6px",
    backdropFilter: "blur(14px)",
    backgroundColor: "rgba(255,255,255,0.78)",
    borderTop: "1px solid rgba(15,23,42,0.06)",
    boxShadow: "0 -6px 30px rgba(15,23,42,0.04)",
    zIndex: 1060,
    touchAction: "manipulation",
  },
  bottomNavItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    padding: "6px 8px",
    minWidth: "56px",
    color: "#111827",
    fontSize: "12px",
    borderRadius: "10px",
    textDecoration: "none",
    background: "transparent",
    WebkitTapHighlightColor: "transparent",
  },
};

const imgStyles = {
  maxHeight: "96px",
  width: "auto",
  objectFit: "contain",
};

// ─── Desktop menu components ──────────────────────────────────────────────────

function EliteMenu({ titleStyle }) {
  return (
    <>
      <Nav.Link to="/sup/" as={NavLink} style={titleStyle}>
        Home
      </Nav.Link>
      <NavDropdown
        title={<span style={titleStyle}>Tables</span>}
        id="elite-tables-dropdown"
      >
        <NavDropdown.Item as={NavLink} to="/sup/orders">Orders</NavDropdown.Item>
        <NavDropdown.Item as={NavLink} to="/sup/stores">Stores</NavDropdown.Item>
        <NavDropdown.Item as={NavLink} to="/ops/deliveries">Riders</NavDropdown.Item>
      </NavDropdown>
      <Nav.Link to="/account" as={NavLink} style={titleStyle}>Account</Nav.Link>
    </>
  );
}

function OperationsMenu({ titleStyle }) {
  return (
    <>
      <Nav.Link to="/ops/" as={NavLink} style={titleStyle}>Home</Nav.Link>
      <Nav.Link to="/ops/deliveries" as={NavLink} style={titleStyle}>Deliveries</Nav.Link>
      <Nav.Link to="/account" as={NavLink} style={titleStyle}>Account</Nav.Link>
    </>
  );
}

function SalesAnalysisMenu({ titleStyle }) {
  return (
    <>
      <Nav.Link to="/sales/dashboard" as={NavLink} style={titleStyle}>Dashboard</Nav.Link>
      <Nav.Link to="/sales/orders" as={NavLink} style={titleStyle}>Orders</Nav.Link>
      <Nav.Link to="/sales/analytics" as={NavLink} style={titleStyle}>Analytics</Nav.Link>
      <Nav.Link to="/account" as={NavLink} style={titleStyle}>Account</Nav.Link>
    </>
  );
}

function MerchantMenu({ titleStyle }) {
  return (
    <>
      <Nav.Link to="/store/dashboard" as={NavLink} style={titleStyle}>Dashboard</Nav.Link>
      <Nav.Link to="/store/orders" as={NavLink} style={titleStyle}>Orders</Nav.Link>
      <Nav.Link to="/account" as={NavLink} style={titleStyle}>Account</Nav.Link>
    </>
  );
}

function CustomerMenu({ titleStyle, baseId, accountClassName = "" }) {
  return (
    <>
      <NavDropdown
        title={<span style={titleStyle}>Stores</span>}
        id={`${baseId}-stores-dropdown`}
      >
        <NavDropdown.Item as={NavLink} to="/">Browse Stores</NavDropdown.Item>
      </NavDropdown>
      <NavDropdown
        title={<span style={titleStyle}>Orders</span>}
        id={`${baseId}-orders-dropdown`}
      >
        <NavDropdown.Item as={NavLink} to="/account/orders">My Orders</NavDropdown.Item>
      </NavDropdown>
      <Nav.Link
        to="/account"
        as={NavLink}
        className={accountClassName}
        style={navStyles.desktopNavLink}
      >
        Account
      </Nav.Link>
    </>
  );
}

// ─── Mobile bottom-nav item ───────────────────────────────────────────────────

function BottomNavItem({ to, label, Icon, isActive }) {
  return (
    <NavLink
      to={to}
      style={isActive ? { ...navStyles.bottomNavItem, color: "#0d6efd" } : navStyles.bottomNavItem}
    >
      <Icon size={18} color={isActive ? "#0d6efd" : "#111827"} />
      <span style={{ fontSize: 11 }}>{label}</span>
    </NavLink>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────

export function Navbar() {
  const { openCart, cartQuantity } = useShoppingCart();
  const { claims, isAuthenticated } = useUserContext();
  const location = useLocation();

  const activeClaim = useMemo(() => {
    if (location.pathname === "/" || CUSTOMER_ROUTE_PREFIXES.some((p) => location.pathname.startsWith(p))) return "customer";
    return isAuthenticated ? claimFromClaims(claims) : claimFromPath(location.pathname);
  }, [isAuthenticated, claims, location.pathname]);

  const bottomNavItems = BOTTOM_NAV_CONFIG[activeClaim] ?? BOTTOM_NAV_CONFIG.customer;

  const activeBottomNavIdx = useMemo(() => {
    let bestIdx = -1;
    let bestLen = -1;
    bottomNavItems.forEach(({ match }, i) => {
      if (location.pathname.startsWith(match) && match.length > bestLen) {
        bestLen = match.length;
        bestIdx = i;
      }
    });
    return bestIdx;
  }, [bottomNavItems, location.pathname]);

  return (
    <>
      <NavbarBs sticky="top" className="shadow-sm mb-3" style={navStyles.navbar}>
        <Container className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2 gap-md-0">
          <div className="d-flex align-items-center w-100">
            <NavbarBs.Brand href="/" className="me-3 me-md-4 mb-0">
              <img
                src={logo}
                alt="logo-image"
                style={{ ...imgStyles, display: "none" }}
                className="d-none d-md-inline-block align-text-top"
              />
              <img
                src={logo}
                alt="logo-image"
                style={{ ...imgStyles, maxHeight: "clamp(48px, 13vw, 60px)" }}
                className="d-inline-block d-md-none align-text-top"
              />
            </NavbarBs.Brand>

            <Nav className="me-auto align-items-center d-none d-md-flex">
              {activeClaim === "elite" && (
                <EliteMenu titleStyle={navStyles.desktopNavLink} />
              )}
              {activeClaim === "operations" && (
                <OperationsMenu titleStyle={navStyles.desktopNavLink} />
              )}
              {activeClaim === "sales" && (
                <SalesAnalysisMenu titleStyle={navStyles.desktopNavLink} />
              )}
              {activeClaim === "merchant" && (
                <MerchantMenu titleStyle={navStyles.desktopNavLink} />
              )}
              {activeClaim === "customer" && (
                <CustomerMenu
                  titleStyle={navStyles.desktopNavLink}
                  baseId="customer-desktop"
                  accountClassName="ms-1"
                />
              )}
            </Nav>

            {activeClaim === "customer" && (
              <div className="d-flex align-items-center ms-auto">
                <Button
                  onClick={openCart}
                  variant="light"
                  className="rounded-circle position-relative ms-2"
                  style={navStyles.cartButton}
                  aria-label="Open cart"
                >
                  <Cart3 color="#0d6efd" size={20} />
                  {cartQuantity > 0 && (
                    <Badge
                      bg="danger"
                      className="rounded-circle d-flex justify-content-center align-items-center"
                      style={navStyles.cartBadge}
                    >
                      {cartQuantity}
                    </Badge>
                  )}
                </Button>
              </div>
            )}
          </div>
        </Container>
      </NavbarBs>

      <div className="d-md-none" style={{ width: "100%" }}>
        <nav style={navStyles.bottomNav} aria-label="Mobile navigation">
          {bottomNavItems.map((item, i) => (
            <BottomNavItem key={item.to} {...item} isActive={i === activeBottomNavIdx} />
          ))}
        </nav>
      </div>
    </>
  );
}
